import { RruleHelper } from '../features/recurrence/rruleHelper'
import { RecurrentFeathersModel } from './RecurrentFeathersModel'
import { DateRange, DateUtils } from '../services/date-utils.service'
import { AssistantAssignmentList } from './assistantAssignment'
import { Client } from './client'
import { Worker } from './worker'
import { addDays, addMilliseconds, isSameMinute, subDays } from 'date-fns'
import { MsToHHMMPipe } from '../shared/pipes/timeconversion.pipe'
import { createIntlComparer, DEFAULT_LOCALES } from '../services/utils'
import { Assistant } from './assistant'

export class AssistanceOccupation {
  dateFrom: Date
  dateTo: Date
  client: Client

  public static sortByStartDate(a: AssistanceOccupation, b: AssistanceOccupation) {
    if (a.dateFrom < b.dateFrom) {
      return -1
    }
    return 1
  }
}

export class WorkerAvailability extends RecurrentFeathersModel {
  static serviceName = 'worker-availabilities'
  static intlSortComparer = createIntlComparer(DEFAULT_LOCALES, {
    sensitivity: 'accent',
  })
  private TAG = 'WorkerAvailability'

  constructor(public id: number = null) {
    super(id)
  }

  workerId?: number
  worker?: Partial<Worker>
  assistanceOccupations?: AssistanceOccupation[]
  dt_start?: Date // Odpovida plannedFrom
  dt_end?: Date // Odpovida plannedTo
  note?: string
  dateStart: Date // Odpovida plannedFrom
  timeStart: Date
  dateEnd: Date
  timeEnd: Date
  remainingVacantTimes: DateRange[]

  assistantAssignments: AssistantAssignmentList
  // Only declared property for DTO reasons
  assignments?: any

  public static expandAvailabilitiesWhichEngulfRange(
    availabilities: WorkerAvailability[],
    engulfedRange: DateRange,
  ) {
    const expanded: WorkerAvailability[] = []

    const limitRange: DateRange = {
      from: subDays(engulfedRange.from, 2),
      to: addDays(engulfedRange.to, 2),
    }

    availabilities.forEach((aa: WorkerAvailability) => {
      if (aa.rrule) {
        expanded.push(
          ...WorkerAvailability.expandAvailabilityByRrules(aa, limitRange).filter((a) => {
            const availabilityRange: DateRange = {
              from: a.getDateFrom(),
              to: a.getDateTo(),
            }
            return (
              DateUtils.isInRange(engulfedRange.from, availabilityRange) &&
              DateUtils.isInRange(engulfedRange.to, availabilityRange)
            )
          }),
        )
      } else {
        expanded.push(aa)
      }
    })
    return expanded
  }

  public static expandAvailabilitiesByRrules(
    availabilities: WorkerAvailability[],
    range?: DateRange,
    expandParentsAsOccurrences = false,
  ) {
    const expanded: WorkerAvailability[] = []

    availabilities.forEach((aa: WorkerAvailability) => {
      if (aa.rrule) {
        // TODO check for RRULE validity
        expanded.push(...WorkerAvailability.expandAvailabilityByRrules(aa, range, expandParentsAsOccurrences))
      } else {
        expanded.push(aa)
      }
    })
    return expanded
  }

  private static expandAvailabilityByRrules(
    parent: WorkerAvailability,
    range?: DateRange,
    expandParentAsOccurrence = false,
  ) {
    const expanded: WorkerAvailability[] = []

    try {
      const occurenceStartDates: Date[] = RruleHelper.expandRecurrentFeathersModel(
        parent as RecurrentFeathersModel,
        range,
      )

      const parentStartDate = parent.getDateFrom()
      const parentDurationMs = parent.getDateTo().getTime() - parent.getDateFrom().getTime()

      // console.debug('expandWorkerAvailabilityByRrules occurrenceStartDate', occurenceStartDates, parent)

      occurenceStartDates.forEach((occurrenceStartDate) => {
        // console.log(occurrenceStartDate)

        if (!expandParentAsOccurrence && isSameMinute(occurrenceStartDate, parentStartDate)) {
          // Means that current occurrence is the first occurrence in the series
          expanded.push(WorkerAvailability.generateFirstOccurrence(parent))
        } else {
          expanded.push(
            WorkerAvailability.generateOccurrence(
              parent,
              occurrenceStartDate,
              addMilliseconds(occurrenceStartDate, parentDurationMs),
            ),
          )
        }
      })

      return expanded
    } catch (e) {
      console.error(parent, e)
      return []
    }
  }

  static expandAvailabilitiesAndFilterByRange(aa: WorkerAvailability[], range: DateRange) {
    return this.expandAvailabilitiesByRrules(aa, range).filter((a) =>
      DateUtils.dateRangeOverlaps(range, a.getPlannedRange()),
    )
  }

  getDateFrom(): Date {
    return this.dt_start
  }
  getDateTo(): Date {
    return this.dt_end
  }

  updateFromProps(props): this {
    super.updateFromProps(props)

    this.dt_start = this.dt_start ? new Date(this.dt_start) : null
    this.dt_end = this.dt_start ? new Date(this.dt_end) : null

    return this
  }

  public setTimes(start?: Date, end?: Date) {
    if (start) {
      this.dt_start = start
    }
    if (end) {
      this.dt_end = end
    }
    this.initTimes()
  }

  public initTimes() {
    if (this.dt_start) {
      this.timeStart = this.dt_start
      this.dateStart = this.dt_start
    }
    if (this.dt_end) {
      this.timeEnd = this.dt_end
      this.dateEnd = this.dt_end
    }
  }

  turnSubresourcesIntoFrontendModels() {
    this.worker = this.worker ? Worker.createFromProps(Worker, this.worker) : this.worker

    // console.log('this.assignments before', this.id, this.assignments, this.assistantAssignments)
    this.convertAssignmentToAssistantAssignmentList()
    // console.log('this.assignments after', this.id, this.assignments, this.assistantAssignments)
  }

  getRemainingVacantTimes(): DateRange[] {
    let remainingVacantDates: DateRange[] = [{ from: this.getDateFrom(), to: this.getDateTo() }]

    if (!this.assistanceOccupations) {
      return remainingVacantDates
    }

    this.assistanceOccupations.forEach((a) => {
      remainingVacantDates = DateUtils.subtractDateRangeFromDateRanges(remainingVacantDates, {
        from: a.dateFrom,
        to: a.dateTo,
      })
    })
    // console.log('remainingVacantDates', remainingVacantDates)
    return remainingVacantDates
  }

  isOccupiedAt(range: DateRange) {
    return DateUtils.dateRangesAnyOverlap(this.getOccupiedTimes(), [range])
  }

  getOccupiedTimes(): DateRange[] {
    if (!this.assistanceOccupations) {
      return []
    }

    const occupiedTimes: DateRange[] = []
    this.assistanceOccupations.forEach((a) => {
      occupiedTimes.push({ from: a.dateFrom, to: a.dateTo })
    })

    return occupiedTimes
  }

  private assistanceOccupationsFromAssistanceAssignments(): AssistanceOccupation[] {
    const occupations: AssistanceOccupation[] = []
    // console.log(this)

    if (this.assistantAssignments) {
      for (const assistance of this.assistantAssignments.getUniqueAssistances()) {
        // console.log(this.TAG, 'assistanceOccupationsFromAssistanceAssignments', assistance)
        const o = new AssistanceOccupation()
        o.dateFrom = assistance.getDateFrom()
        o.dateTo = assistance.getDateTo()
        o.client = assistance.client
        occupations.push(o)
      }
    }

    // console.log('assistanceOccupationsFromAssistanceAssignments', this);
    //
    return occupations
  }

  public convertAssignmentToAssistantAssignmentList() {
    if (this.assignments) {
      this.assistantAssignments = AssistantAssignmentList.initializeAsFeathersModels(this.assignments)
      this.assignments = null
      delete this.assignments
    }
  }

  public static convertAssignmentToAssistantAssignmentList(workerAvailability: WorkerAvailability) {
    if (workerAvailability.assignments) {
      workerAvailability.assistantAssignments = AssistantAssignmentList.initializeAsFeathersModels(
        workerAvailability.assignments,
      )
      workerAvailability.assignments = null
      delete workerAvailability.assignments
    }
    return workerAvailability
  }

  isAllowedToBeDeleted() {
    // console.log('this.assistanceOccupations', this.assistanceOccupations, this)
    return !this.assistanceOccupations || this.assistanceOccupations?.length === 0 || this.isRecurrentButNotException()
  }

  static sortByAssistantName(a: WorkerAvailability, b: WorkerAvailability) {
    return WorkerAvailability.intlSortComparer(
      a.worker?.user?.fullName(),
      b.worker?.user?.fullName(),
    )
  }

  static generateOccurrence(parent: WorkerAvailability, startDate: Date, endDate: Date) {
    const availability = WorkerAvailability.createFromProps(WorkerAvailability, parent)
    availability.id = null

    availability.setTimes(startDate, endDate)

    availability.recurrenceParentId = parent.id
    availability.recurrenceException = null
    availability.rrule = null
    availability.recurrenceExceptionDate = availability.getDateFrom()

    return availability
  }

  static generateFirstOccurrence(parent: WorkerAvailability) {
    const availability = WorkerAvailability.createFromProps(WorkerAvailability, parent)
    availability.recurrenceParentId = null

    return availability
  }

  getDurationMs(): number {
    if (!this.dt_start || !this.dt_end) return 0
    return this.dt_end.getTime() - this.dt_start.getTime()
  }

  getDurationMinutes(): number {
    const ms = this.getDurationMs()
    return ms / 1000 / 60
  }

  getDurationHours(): string {
    return (this.getDurationMinutes() / 60).toFixed(2)
  }

  static sumDurationHHMM(availabilities: WorkerAvailability[]): string {
    if (!availabilities) return '00:00'
    const durationMsTotal = availabilities.reduce((total, curr) => {
      return total + curr.getDurationMs()
    }, 0)
    return new MsToHHMMPipe().transform(durationMsTotal)
  }

  static sumDurationHHDecimalMinutes(availabilities: WorkerAvailability[], decimalPlaces = 2) {
    if (!availabilities) return '00.00'
    const durationMsTotal = availabilities.reduce((total, curr) => {
      return total + curr.getDurationMs()
    }, 0)
    return (durationMsTotal / 1000 / 60 / 60).toFixed(2)
    // return new MsToHHMMPipe().transform(durationMsTotal)
  }

  generateFirstOccurrence(): WorkerAvailability {
    return WorkerAvailability.generateFirstOccurrence(this)
  }

  generateOccurrence(occurrenceStartDate: Date, occurrenceEndDate: Date): WorkerAvailability {
    return WorkerAvailability.generateOccurrence(this, occurrenceStartDate, occurrenceEndDate)
  }
}
